// HM-345+349 cluster 16.1.5: chrome wizard walk-in (header + stepper +
// footer) pixel-aware vs docs/design-references/walkin-wizard-mockup-v3.html.
//
// Modal wrapper: max-width 820px, radius 16, shadow 0 24 64 rgba warm,
// overlay rgba(45,36,29,0.5).
// Header: icon-bg arancio + title 18/700 + subtitle dinamico camera/edif/piano.
// Stepper: 4 cerchi rotondi 32x32 con stati active (arancio + shadow ring) /
// completed (verde + check) / skipped (dashed grigio + arrow icon).
// Footer uniforme con info "Step N di 4 · {label}" + Indietro + Avanti
// (testo dinamico per Step 4 "Chiudi" e Step 3 "Conferma e paga").
//
// Step 1 INVARIATO post cluster 16.1 (StepSelectRoom dynamic).

import { BedDouble, Check, ChevronLeft, ChevronRight, X } from '@hotel-manager/ui/icons';
import { cn } from '@hotel-manager/ui/lib/cn';
import { buildingLabelOf } from '../../lib/buildingLabel.js';
import { useWalkInWizard } from '../../stores/walkIn.js';
import { ErrorRecovery } from './ErrorRecovery.js';
import { StepGuestInfo } from './StepGuestInfo.js';
import { StepPayment } from './StepPayment.js';
import { StepRecap } from './StepRecap.js';
import { StepSelectRoom } from './StepSelectRoom.js';

const STEP_LABELS = ['Camera', 'Ospite', 'Pagamento', 'Conferma'] as const;

export function WalkInModal() {
  const isOpen = useWalkInWizard((s) => s.isOpen);
  const room = useWalkInWizard((s) => s.room);
  const cascade = useWalkInWizard((s) => s.cascade);

  if (!isOpen || !room) return null;

  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: overlay manuale; dialog elem ha popover semantics non adatte.
      role="dialog"
      aria-modal="true"
      aria-labelledby="walk-in-title"
      data-testid="walk-in-modal"
      className="fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto p-5"
      style={{ background: 'rgba(45, 36, 29, 0.5)' }}
    >
      <div
        className="bg-bg-card rounded-2xl overflow-hidden mt-7 mb-7 max-h-[90vh] flex flex-col"
        style={{
          width: 'min(820px, 96vw)',
          boxShadow: '0 24px 64px rgba(45, 36, 29, 0.25)',
        }}
      >
        <ModalChrome roomCode={room.code} roomBuilding={room.building} roomFloor={room.floor} />
        <Stepper />
        <ModalBody />
        {cascade.error && <ErrorRecovery />}
        <WizardFooter />
      </div>
    </div>
  );
}

// --------------------------- ModalChrome ---------------------------

function ModalChrome({
  roomCode,
  roomBuilding,
  roomFloor,
}: {
  roomCode: string;
  roomBuilding: string;
  roomFloor: string;
}) {
  const close = useWalkInWizard((s) => s.close);
  return (
    <header className="shrink-0 flex items-center justify-between px-7 py-5 border-b border-border-base">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-[10px] bg-accent-soft flex items-center justify-center">
          <BedDouble size={22} className="text-accent-primary" />
        </span>
        <div>
          <h2 id="walk-in-title" className="text-lg font-bold text-text-primary tracking-[-0.01em]">
            Nuovo Check-in
          </h2>
          <p className="text-xs text-text-secondary mt-0.5">
            Camera {roomCode} · {buildingLabelOf(roomBuilding)} · Piano {roomFloor}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={close}
        aria-label="Chiudi"
        data-testid="walkin-close"
        className="h-11 w-11 min-h-[44px] min-w-[44px] rounded-lg bg-bg-card border border-border-base flex items-center justify-center text-text-secondary hover:bg-bg-base"
      >
        <X size={18} />
      </button>
    </header>
  );
}

// --------------------------- Stepper ---------------------------

function Stepper() {
  const step = useWalkInWizard((s) => s.step);
  const isAnonymous = useWalkInWizard((s) => s.isAnonymous);

  // Skipped: solo Step 2 puo' essere skipped quando l'utente sceglie il flow
  // anonymous Day Use e ci si trova oltre Step 2.
  const stepIsSkipped = (n: number) => n === 2 && isAnonymous && step > 2;

  return (
    <div
      className="shrink-0 flex items-center px-7 py-6 border-b border-border-base gap-2"
      style={{ background: 'linear-gradient(180deg, #FAF7F2 0%, white 100%)' }}
      data-testid="wizard-stepper"
    >
      {[1, 2, 3, 4].map((n, idx) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: indice stabile (4 step fissi)
        <div key={n} className="contents">
          <StepIndicator
            n={n}
            label={STEP_LABELS[n - 1] ?? ''}
            state={
              stepIsSkipped(n)
                ? 'skipped'
                : n < step
                  ? 'completed'
                  : n === step
                    ? 'active'
                    : 'pending'
            }
          />
          {idx < 3 && (
            <div
              data-testid={`stepper-connector-${n}`}
              className="flex-1 h-0.5 rounded-sm mx-1"
              style={{
                background:
                  n === 1 && stepIsSkipped(2) ? '#D4C7B5' : n < step ? '#8BAE7C' : '#E8DFD1',
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function StepIndicator({
  n,
  label,
  state,
}: {
  n: number;
  label: string;
  state: 'active' | 'completed' | 'skipped' | 'pending';
}) {
  return (
    <div
      data-testid={`step-indicator-${n}`}
      data-state={state}
      className="flex items-center gap-2.5 flex-1"
    >
      <span
        className={cn(
          'w-8 h-8 rounded-full border-2 flex items-center justify-center text-[13px] font-semibold flex-shrink-0 transition-all',
          state === 'pending' && 'bg-bg-card border-border-base text-text-muted',
          state === 'active' &&
            'bg-accent-primary border-accent-primary text-text-on-accent shadow-[0_0_0_4px_#FCF1EA]',
          state === 'completed' && 'border-[#8BAE7C] text-text-on-accent',
          state === 'skipped' && 'border-dashed border-border-input text-text-muted',
        )}
        style={{
          background:
            state === 'completed' ? '#8BAE7C' : state === 'skipped' ? '#F2EBDF' : undefined,
        }}
      >
        {state === 'completed' ? (
          <Check size={14} />
        ) : state === 'skipped' ? (
          <ChevronRight size={12} />
        ) : (
          n
        )}
      </span>
      <div className="flex flex-col leading-tight min-w-0">
        <span
          className={cn(
            'text-[11px] uppercase tracking-[0.5px] font-semibold',
            state === 'active' || state === 'completed' ? 'text-text-primary' : 'text-text-muted',
          )}
        >
          Step {n}
        </span>
        <span
          className={cn(
            'text-[13px] font-semibold truncate',
            state === 'active'
              ? 'text-text-primary'
              : state === 'completed'
                ? 'text-[#5F8A50]'
                : state === 'skipped'
                  ? 'text-text-muted italic'
                  : 'text-text-muted',
          )}
        >
          {state === 'skipped' ? 'Skippato' : label}
        </span>
      </div>
    </div>
  );
}

// --------------------------- ModalBody ---------------------------

function ModalBody() {
  const step = useWalkInWizard((s) => s.step);
  return (
    <div className="px-8 py-8 min-h-[380px] flex-1 overflow-y-auto" data-testid="wizard-body">
      {step === 1 && <StepSelectRoom />}
      {step === 2 && <StepGuestInfo />}
      {step === 3 && <StepPayment />}
      {step === 4 && <StepRecap />}
    </div>
  );
}

// --------------------------- WizardFooter ---------------------------

function WizardFooter() {
  const step = useWalkInWizard((s) => s.step);
  const setStep = useWalkInWizard((s) => s.setStep);
  const close = useWalkInWizard((s) => s.close);
  const setAnonymous = useWalkInWizard((s) => s.setAnonymous);
  const isAnonymous = useWalkInWizard((s) => s.isAnonymous);
  const payment = useWalkInWizard((s) => s.payment);

  // Step 1 ha il proprio footer interno (StepSelectRoom cluster 16.1).
  // Skip footer globale per Step 1 per non duplicare.
  if (step === 1) return null;

  const onPrev = () => {
    if (step === 3 && isAnonymous) {
      setAnonymous(false);
      setStep(2);
    } else if (step > 1) {
      setStep((step - 1) as 1 | 2 | 3 | 4);
    }
  };

  // Revisione pagamenti 2026-07-22: con "Paga all'uscita" niente incasso ora.
  const confirmLabel = payment === 'later' ? 'Conferma senza incasso' : 'Conferma e paga';
  const nextLabel = step === 4 ? 'Chiudi' : step === 3 ? confirmLabel : 'Avanti';
  const onNext = step === 4 ? close : () => setStep((step + 1) as 1 | 2 | 3 | 4);

  return (
    <footer
      className="shrink-0 bg-bg-card border-t border-border-base flex items-center justify-between px-7 py-4"
      data-testid="wizard-footer"
    >
      <span className="text-xs text-text-secondary" data-testid="wizard-footer-info">
        Step {step} di 4 · {STEP_LABELS[step - 1]}
      </span>
      <div className="flex gap-2.5">
        <button
          type="button"
          onClick={onPrev}
          data-testid="wizard-back"
          className="min-h-[44px] bg-bg-card text-text-secondary border border-border-base px-5 py-2.5 rounded-lg text-[13px] font-semibold flex items-center gap-1.5 hover:bg-bg-base"
        >
          <ChevronLeft size={15} />
          Indietro
        </button>
        <button
          type="button"
          onClick={onNext}
          data-testid="wizard-next"
          className="min-h-[44px] bg-accent-primary text-text-on-accent border-0 px-7 py-2.5 rounded-lg text-[13px] font-semibold flex items-center gap-1.5 hover:bg-accent-primary-hover hover:-translate-y-px transition-all"
          style={{ boxShadow: '0 2px 8px rgba(233, 123, 61, 0.25)' }}
        >
          {nextLabel}
          {step === 4 ? <Check size={15} /> : <ChevronRight size={15} />}
        </button>
      </div>
    </footer>
  );
}
